import React, { useEffect } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { PageBanner } from "@performyard/py-components";

import ChatErrorBanner from "./ChatErrorBanner";
import ChatMessage from "./ChatMessage";
import { CompetencyImportWatcher } from "./CompetencyImportWatcher";

import { useCompetencyDocumentUpload } from "../hooks/useCompetencyDocumentUpload";

import { isCompetencyReimportBlocked } from "../utils/is-competency-reimport-blocked";
import { isUploadInFlight } from "../utils/is-upload-in-flight";

import { useAskPerformYardStore } from "../AskPerformYardStore";

/** Registers the scrollToBottom function in the store so other components can use it. */
const ScrollToBottomSync: React.FC = () => {
	const { scrollToBottom } = useStickToBottomContext();
	const setScrollToBottom = useAskPerformYardStore((state) => state.setScrollToBottom);

	useEffect(() => {
		setScrollToBottom(() => scrollToBottom());
		return () => setScrollToBottom(null);
	}, [scrollToBottom, setScrollToBottom]);

	return null;
};

/** Scroll-to-bottom button that appears when user has scrolled up. */
const ScrollToBottomButton: React.FC<{ hasError: boolean }> = ({ hasError }) => {
	const { isAtBottom, scrollToBottom } = useStickToBottomContext();

	if (isAtBottom) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={() => scrollToBottom()}
			className={`absolute left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-neutral-3 bg-white transition-all hover:bg-neutral-1 ${hasError ? "bottom-14" : "bottom-4"}`}
			aria-label="Scroll to bottom"
		>
			<FontAwesomeIcon icon={faArrowDown} className="h-3 w-3 text-neutral-5" />
		</button>
	);
};

type ChatMessagesProps = {
	/** "panel" renders the store error in-flow as a dismissible PageBanner after the thread (instead of the absolutely-positioned overlay), skips the overlay's extra bottom spacing, and filters the synthetic "greeting" message out of the thread. Default "floating" keeps the existing FAB behaviour byte-identical: `variant !== "panel"` reduces to the old `!errorInline`/`!hideGreeting` everywhere those flags were read. */
	variant?: "floating" | "panel";
	/** Retries the last user message. Rendered as the panel error banner's "Try Again" action when set and a user message exists; has no effect in the floating variant. */
	onRetry?: () => void;
};

export const ChatMessages: React.FC<ChatMessagesProps> = ({ variant = "floating", onRetry }) => {
	const messages = useAskPerformYardStore((state) => state.messages);
	const error = useAskPerformYardStore((state) => state.error);
	const setError = useAskPerformYardStore((state) => state.setError);
	const status = useAskPerformYardStore((state) => state.status);
	const { fileInputRef, triggerUpload, handleFileSelected } = useCompetencyDocumentUpload();

	const isPanel = variant === "panel";
	const hasError = !!error;
	const hasUserMessage = messages.some((message) => message.role === "user");
	const visibleMessages = isPanel ? messages.filter((message) => message.id !== "greeting") : messages;
	const uploadDisabled = status === "thinking" || status === "streaming" || isUploadInFlight(messages);
	const competencyFramework = useAskPerformYardStore((state) => state.competencyFramework);
	const uploadBlocked = isCompetencyReimportBlocked(competencyFramework);

	return (
		<StickToBottom className="relative flex-1 overflow-hidden" resize="smooth" initial="smooth">
			<StickToBottom.Content
				scrollClassName="![scrollbar-gutter:auto]"
				className={`flex flex-col items-start gap-2 ${isPanel ? "px-5 py-2" : "p-2 pr-4 pl-3"} ${hasError && !isPanel ? "pb-12" : ""}`}
			>
				<input
					type="file"
					ref={fileInputRef}
					accept="application/pdf"
					onChange={(event) => {
						const file = event.target.files?.[0];
						event.target.value = "";
						if (file) void handleFileSelected(file);
					}}
					className="hidden"
				/>
				{visibleMessages.map((message) => (
					<ChatMessage
						key={message.id}
						message={message}
						variant={variant}
						onCompetencyUpload={uploadBlocked ? undefined : triggerUpload}
						competencyUploadDisabled={uploadDisabled}
					/>
				))}

				{error && isPanel && (
					<div className="box-border w-full px-1">
						<PageBanner
							color="red"
							size="small"
							mainTitle={error}
							onDismiss={() => setError(null)}
							data-testid="ask-py-error-banner"
							rightActionButton={
								onRetry && hasUserMessage ? { children: "Try Again", onClick: onRetry } : undefined
							}
						/>
					</div>
				)}
			</StickToBottom.Content>

			{error && !isPanel && (
				<div className="absolute right-0 bottom-0 left-0 z-10 px-4 pb-2">
					<ChatErrorBanner type="error" message={error} />
				</div>
			)}

			{messages
				.flatMap((message) => {
					const attachment = message.attachment;
					return attachment?.importTaskId && attachment.narrationMessageId && !attachment.importComplete
						? [
								{
									id: message.id,
									importTaskId: attachment.importTaskId,
									narrationMessageId: attachment.narrationMessageId,
								},
							]
						: [];
				})
				.map(({ id, importTaskId, narrationMessageId }) => (
					<CompetencyImportWatcher
						key={importTaskId}
						taskId={importTaskId}
						attachmentMessageId={id}
						narrationMessageId={narrationMessageId}
					/>
				))}

			<ScrollToBottomSync />
			<ScrollToBottomButton hasError={hasError && !isPanel} />
		</StickToBottom>
	);
};

export default ChatMessages;
